import { HttpResponse, PayLinkClient, Transport } from '@baemingo/loomis-pay';

/**
 * The port Teller PayLink, the payment API on a Loomis Pay terminal, serves on.
 * The terminal's IP is on Teller's Status Screen > Network Settings.
 */
const LOOMIS_PORT = 50333;

/**
 * Some time beyond a request's own timeout for the bridge to answer. The
 * bridge never times out, and a wrapper without FETCH never answers at all.
 */
const BRIDGE_SLACK_MS = 5_000;

type BridgeFetch = (
  url: string,
  options?: { method?: string; headers?: Record<string, string>; body?: string },
) => Promise<
  { status: number; headers: Record<string, string>; body: string } | { error: string }
>;

/**
 * PayLink through the native wrapper's FETCH, on the iPad or handheld OM runs
 * on. The WebView cannot reach Teller itself: its certificate is self-signed
 * and its CORS preflight is refused. The wrapper trusts any certificate
 * (`trusty`).
 */
const bridgeTransport = (fetch: BridgeFetch): Transport => (request) =>
  new Promise<HttpResponse>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('The app did not answer the request')),
      request.timeoutMs + BRIDGE_SLACK_MS,
    );

    fetch(request.url, {
      method: request.method,
      headers: request.headers,
      body: request.body,
    }).then(
      (result) => {
        clearTimeout(timer);

        if (!result) {
          reject(new Error('This build of the app cannot make HTTP requests'));
        } else if ('error' in result) {
          reject(new Error(result.error));
        } else {
          const headers: Record<string, string> = {};
          Object.entries(result.headers ?? {}).forEach(([key, value]) => {
            headers[key.toLowerCase()] = String(value);
          });
          resolve({ status: result.status, headers, body: result.body ?? '' });
        }
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

/** A PayLink client for the terminal at `ip`, which may include a port. */
export const createLoomisClient = (fetch: BridgeFetch, ip: string) =>
  new PayLinkClient({
    baseUrl: `https://${ip.includes(':') ? ip : `${ip}:${LOOMIS_PORT}`}`,
    apiKey: process.env.LOOMIS_API_KEY ?? '',
    transport: bridgeTransport(fetch),
  });
